// Thin fetch wrapper for talking to the RABS backend.
//
// Usage:
//   import api from "./api";
//   const domains = await api.get("/domains");
//   const created = await api.post("/domains", { name: "example.com" });
//   await api.put(`/domains/${id}`, { ... });
//   await api.delete(`/domains/${id}`);
//
// Auth:
//   The JWT is read from localStorage("authToken") on every call. The
//   AuthContext stores it there on successful login. If the server returns
//   401, we clear the token + redirect to the login page so the user can
//   sign in again.

// API base URL.
//   - Flip the commented line below when building for production.
// const API_URL =  "https://api.workspace.rabs.in.net";
const API_URL = "https://api.workspace.rabs.in.net";


const API_PREFIX = "/api";

function buildUrl(path) {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${API_URL}${API_PREFIX}${clean}`;
}

function getToken() {
  return localStorage.getItem("authToken") || "";
}

async function request(method, path, body) {
  const headers = {
    Accept: "application/json",
  };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let payload;
  if (body instanceof FormData) {
    // File upload: send the FormData as-is. The browser sets Content-Type
    // (with the multipart boundary) — setting it by hand breaks the upload.
    payload = body;
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  

  let response;
  try {
    response = await fetch(buildUrl(path), {
      method,
      headers,
      body: payload,
      // Never serve API responses from the browser's HTTP cache. Otherwise an
      // in-app refetch (after add/edit/delete) can return a stale cached list,
      // so new rows only appear after a full page reload.
      cache: "no-store",
    });
  } catch (err) {
    // Network-level failure (server down, CORS, etc.)
    const e = new Error(
      "Could not reach the API. Is the backend running on " + API_URL + "?"
    );
    e.code = "NETWORK_ERROR";
    e.cause = err;
    throw e;
  }

  // 204 No Content has no body
  let data = null;
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!response.ok) {
    // Auth failures: clear the bad token and notify the AuthContext via
    // a custom event so it can redirect to login.
    if (response.status === 401) {
      localStorage.removeItem("authToken");
      window.dispatchEvent(new CustomEvent("auth:expired"));
    }
    const error = new Error(
      (data && data.error) ||
        (data && data.message) ||
        `Request failed: ${response.status}`
    );
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

const api = {
  get: (path) => request("GET", path),
  post: (path, body) => request("POST", path, body ?? {}),
  put: (path, body) => request("PUT", path, body ?? {}),
  patch: (path, body) => request("PATCH", path, body ?? {}),
  delete: (path) => request("DELETE", path),
  // multipart/form-data POST (ZIP upload → GitHub repo).
  upload: (path, formData) => request("POST", path, formData),
  baseUrl: API_URL,
};

export default api;
